import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Navigation, Play, Download } from 'lucide-react';

interface NavigatorLaunchDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    destination?: {
        lat: number;
        lng: number;
        name?: string;
    } | null;
}

export function NavigatorLaunchDialog({ open, onOpenChange, destination }: NavigatorLaunchDialogProps) {
    // TODO: Replace with actual URLs provided by client
    const DEEP_LINK_SCHEME = 'navigatorx://navigate';
    const ANDROID_STORE_URL = 'https://play.google.com/store/apps/details?id=com.oilwellx.navigatorx';
    const IOS_STORE_URL = 'https://apps.apple.com/us/app/navigatorx/id123456789';

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);

    const handleLaunch = () => {
        // Construct deep link with parameters if destination exists
        let deepLink = DEEP_LINK_SCHEME;
        if (destination) {
            deepLink += `?lat=${destination.lat}&lng=${destination.lng}&name=${encodeURIComponent(destination.name || 'Destination')}`;
        }

        // Attempt to open the app
        window.location.href = deepLink;
    };

    const handleInstall = () => {
        if (isIOS) {
            window.open(IOS_STORE_URL, '_blank');
        } else {
            window.open(ANDROID_STORE_URL, '_blank');
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md bg-white dark:bg-slate-900 border-0 shadow-2xl">
                <DialogHeader className="space-y-4 items-center text-center pb-2">
                    <div className="w-16 h-16 bg-blue-500/10 rounded-2xl flex items-center justify-center mb-2 animate-bounce-slow">
                        <Navigation className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                    </div>
                    <DialogTitle className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-cyan-500">
                        Launch NavigatorX
                    </DialogTitle>
                    <DialogDescription className="text-center max-w-xs text-slate-600 dark:text-slate-400">
                        Advanced navigation features have moved to the dedicated NavigatorX app for a better experience.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid gap-4 py-4">
                    <Button
                        size="lg"
                        onClick={handleLaunch}
                        className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white shadow-lg shadow-blue-500/25 h-14 text-lg"
                    >
                        <Play className="w-5 h-5 mr-2 fill-current" />
                        Launch App
                    </Button>

                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <span className="w-full border-t border-slate-200 dark:border-slate-800" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-white dark:bg-slate-900 px-2 text-slate-500">
                                Not installed?
                            </span>
                        </div>
                    </div>

                    <Button
                        variant="outline"
                        size="lg"
                        onClick={handleInstall}
                        className="w-full border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800"
                    >
                        <Download className="w-5 h-5 mr-2 text-slate-500" />
                        Install NavigatorX
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
